// "Remind me" on a ticket: pick when, say whether it only matters if the client stays
// quiet, add a note. See lib/reminders.ts for the times and migration 082 for what fires.

import { useEffect, useState } from 'react';
import { AlarmClock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { describeWhen, reminderPresets, type ReminderPresetKey } from '@/lib/reminders';
import { useClearReminder, useSetReminder, useTicketReminder } from '@/lib/use-reminders';
import { toastSuccess, toastValidation } from '@/lib/toast';
import { cn } from '@/lib/utils';

function toLocalInput(d: Date): string {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}

export function ReminderDialog({
  ticketId,
  open,
  onOpenChange,
}: {
  ticketId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const existing = useTicketReminder(ticketId);
  const set = useSetReminder();
  const clear = useClearReminder();
  const [preset, setPreset] = useState<ReminderPresetKey | 'custom'>('2d');
  const [custom, setCustom] = useState('');
  const [onlyIfNoReply, setOnlyIfNoReply] = useState(true);
  const [note, setNote] = useState('');

  // Start from what is already set, or from the usual "in 2 days if no reply".
  useEffect(() => {
    if (!open) return;
    if (existing) {
      setPreset('custom');
      setCustom(toLocalInput(new Date(existing.remind_at)));
      setOnlyIfNoReply(existing.only_if_no_reply);
      setNote(existing.note ?? '');
    } else {
      setPreset('2d');
      setCustom('');
      setOnlyIfNoReply(true);
      setNote('');
    }
  }, [open, existing]);

  const presets = reminderPresets(new Date());

  const save = () => {
    let when: Date;
    if (preset === 'custom') {
      when = new Date(custom);
      if (!custom || Number.isNaN(when.getTime()) || when.getTime() <= Date.now()) {
        return toastValidation('Pick a time in the future');
      }
    } else {
      when = presets.find((p) => p.key === preset)!.at;
    }
    set.mutate(
      { ticketId, remindAt: when, onlyIfNoReply, note },
      {
        onSuccess: () => {
          onOpenChange(false);
          toastSuccess('Reminder set', `You'll hear about this ${describeWhen(when)}.`);
        },
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlarmClock className="h-4 w-4" aria-hidden /> Remind me about this ticket
          </DialogTitle>
          <DialogDescription>
            The ticket stays where it is (unlike Snooze). You get a notification, and an email if you have those on,
            when the time comes.
            {existing ? ` Current reminder: ${describeWhen(existing.remind_at)}.` : ''}
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-3">
          <div className="grid grid-cols-2 gap-2">
            {presets.map((p) => (
              <button
                key={p.key}
                type="button"
                onClick={() => setPreset(p.key)}
                className={cn(
                  'flex flex-col items-start rounded-md border px-3 py-2 text-left transition-colors',
                  preset === p.key ? 'border-foreground bg-foreground/5' : 'border-border hover:border-foreground/40 hover:bg-accent',
                )}
              >
                <span className="text-sm font-medium">{p.label}</span>
                <span className="text-2xs text-muted-foreground">{describeWhen(p.at)}</span>
              </button>
            ))}
            <button
              type="button"
              onClick={() => setPreset('custom')}
              className={cn(
                'flex flex-col items-start rounded-md border px-3 py-2 text-left transition-colors',
                preset === 'custom' ? 'border-foreground bg-foreground/5' : 'border-border hover:border-foreground/40 hover:bg-accent',
              )}
            >
              <span className="text-sm font-medium">Pick a time…</span>
              <span className="text-2xs text-muted-foreground">Any date and time</span>
            </button>
          </div>

          {preset === 'custom' ? (
            <div>
              <Label htmlFor="reminder-custom" className="text-2xs font-medium uppercase tracking-wide text-muted-foreground">
                Remind me at
              </Label>
              <input
                id="reminder-custom"
                type="datetime-local"
                value={custom}
                min={toLocalInput(new Date(Date.now() + 60_000))}
                onChange={(e) => setCustom(e.target.value)}
                className="mt-1 h-9 w-full rounded-md border border-border bg-background px-2 text-sm"
              />
            </div>
          ) : null}

          <label className="flex cursor-pointer items-start gap-2 text-sm">
            <input
              type="checkbox"
              checked={onlyIfNoReply}
              onChange={(e) => setOnlyIfNoReply(e.target.checked)}
              className="mt-0.5 h-4 w-4 cursor-pointer accent-foreground"
            />
            <span>
              Only if the client hasn't replied
              <span className="block text-2xs text-muted-foreground">
                If they write back first, the reminder is dropped: you'll hear about their reply anyway.
              </span>
            </span>
          </label>

          <div>
            <Label htmlFor="reminder-note" className="text-2xs font-medium uppercase tracking-wide text-muted-foreground">
              Note to yourself (optional)
            </Label>
            <Input
              id="reminder-note"
              value={note}
              maxLength={300}
              onChange={(e) => setNote(e.target.value)}
              placeholder="e.g. chase for the signed form"
              className="mt-1"
            />
          </div>
        </div>

        <DialogFooter className="gap-2 sm:justify-between">
          {existing ? (
            <Button
              type="button"
              variant="ghost"
              disabled={clear.isPending}
              onClick={() => clear.mutate(existing.id, { onSuccess: () => onOpenChange(false) })}
            >
              Remove reminder
            </Button>
          ) : (
            <span />
          )}
          <span className="flex gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="button" disabled={set.isPending} onClick={save}>
              {existing ? 'Update reminder' : 'Set reminder'}
            </Button>
          </span>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
